import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { live, toChargePoint } from '../data/live';
import type { CommandResult, LiveSite, Source } from '../data/live';
import type { ChargePoint } from '../data/types';

/* Every screen reads through these.
 *
 * There is no seed and no sample mode: the console shows what the servers hold
 * or it says it cannot reach them. A console that quietly serves plausible
 * numbers during an outage is worse than one that goes blank, because nobody
 * can tell the two states apart.
 *
 * Keys are coarse on purpose. The console is small enough that invalidating a
 * whole list after a mutation is cheaper to reason about than surgical cache
 * edits, and it cannot go stale. */

/** The site level. Changes rarely, so it does not poll. */
export const useStations = () =>
  useQuery({
    queryKey: ['stations'],
    queryFn: async (): Promise<LiveSite[]> => (await live.stations()).items,
  });

export const useChargePoints = (fast = false) =>
  useQuery({
    queryKey: ['chargepoints'],
    queryFn: async (): Promise<{ items: ChargePoint[]; source: Source }> => {
      const r = await live.chargePoints();
      return { items: r.items.map(toChargePoint), source: 'live' };
    },
    // A charge takes a few seconds to appear after the command is accepted, so
    // the test bench watches closely while it is open. Nothing else needs to.
    refetchInterval: fast ? 3000 : 20_000,
  });

export const useTransactions = () =>
  useQuery({ queryKey: ['transactions'], queryFn: () => live.transactions() });

export const useConnections = () =>
  useQuery({ queryKey: ['connections'], queryFn: () => live.connections(), refetchInterval: 20_000 });

export const useAgents = () =>
  useQuery({ queryKey: ['agents'], queryFn: async () => (await live.agents()).items });

export const useActivity = (q: { area?: string; q?: string } = {}) =>
  useQuery({
    queryKey: ['activity', q.area ?? 'all', q.q ?? ''],
    // Filtering server-side keeps the console honest once the log is long: the
    // counts and the rows then come from the same place.
    queryFn: async () => (await live.activity(q)).items,
  });

export const useApiUsage = (hours: number) =>
  useQuery({ queryKey: ['api-usage', hours], queryFn: () => live.apiUsage(hours), refetchInterval: 30_000 });

export const useActivitySummary = () =>
  useQuery({ queryKey: ['activity-summary'], queryFn: () => live.activitySummary() });

export const useReleases = () =>
  useQuery({ queryKey: ['releases'], queryFn: async () => (await live.releases()).items });

/** OCPP traffic. Polls only while the screen asks it to. */
export const useFrames = (
  q: { action?: string; direction?: string; chargePointId?: string },
  polling: boolean,
) =>
  useQuery({
    queryKey: ['frames', q.action ?? 'all', q.direction ?? 'all', q.chargePointId ?? 'all'],
    queryFn: async () => (await live.frames(q)).items,
    refetchInterval: polling ? 4000 : false,
  });

export const useFrameActions = () =>
  useQuery({ queryKey: ['frame-actions'], queryFn: async () => (await live.frameActions()).items });

export const useDrivers = (q = '') =>
  useQuery({ queryKey: ['drivers', q], queryFn: async () => (await live.drivers(q)).items });

export const useDisputes = (state = 'all') =>
  useQuery({ queryKey: ['disputes', state], queryFn: async () => (await live.disputes(state)).items });

export const useDriver = (id: string | null) =>
  useQuery({
    queryKey: ['driver', id],
    queryFn: () => live.driver(id!),
    enabled: !!id,
  });

/** Drift between our idTags and the CSMS's Authorizations. Polled rather than
 *  fetched once: it is a health check, and a stale one is worse than none. */
export const useAuthReport = () =>
  useQuery({ queryKey: ['auth-report'], queryFn: () => live.authorizations(), refetchInterval: 60_000 });

export const useConsoleUsers = () =>
  useQuery({ queryKey: ['console-users'], queryFn: async () => (await live.users()).items });

/** What the network actually did over a window. Not polled: a report you are
 *  reading should not renumber itself under you. */
export const useUsage = (from?: string, to?: string) =>
  useQuery({
    queryKey: ['usage', from ?? '', to ?? ''],
    queryFn: () => live.usage({ from, to }),
  });

export const useConfig = () =>
  useQuery({ queryKey: ['config'], queryFn: async () => (await live.config()).items });

/* ----------------------------------------------------------- mutations --- */

/** Every write touches the audit trail, so both lists are refetched. */
function useAfterWrite(...keys: string[]) {
  const qc = useQueryClient();
  return () => {
    for (const k of [...keys, 'activity', 'activity-summary']) {
      void qc.invalidateQueries({ queryKey: [k] });
    }
  };
}

export function useChargePointMutations() {
  // A charge point carries its site, so both lists move together.
  const after = useAfterWrite('chargepoints', 'stations');
  return {
    block: useMutation({
      mutationFn: (v: { id: string; reason: string }) =>
        live.blockChargePoint(v.id, true, v.reason),
      onSuccess: after,
    }),
    unblock: useMutation({
      mutationFn: (id: string) => live.blockChargePoint(id, false),
      onSuccess: after,
    }),
    update: useMutation({
      mutationFn: (v: {
        id: string; stationId?: string | null; displayName?: string | null;
        maxSimultaneous?: number | null; priceMinor?: number | null; currency?: string | null;
        environment?: 'test' | 'prod';
        /* The hardware, as an operator records it. Null clears the override
           and the charger's own BootNotification value shows through again. */
        manufacturer?: string | null; model?: string | null; serialNumber?: string | null;
      }) => {
        const { id, ...rest } = v;
        return live.updateChargePoint(id, rest);
      },
      onSuccess: after,
    }),
    remove: useMutation({
      mutationFn: (v: { cpId: string; confirm: string; includeRecords?: boolean }) =>
        live.removeChargePoint(v.cpId, v.confirm, v.includeRecords ?? false),
      onSuccess: after,
    }),
    preRegister: useMutation({
      mutationFn: (v: { chargePointId: string; displayName?: string; stationId?: string }) =>
        live.preRegisterChargePoint(v),
      onSuccess: after,
    }),
  };
}

export function useStationMutations() {
  const after = useAfterWrite('stations', 'chargepoints');
  return {
    create: useMutation({
      mutationFn: (v: Partial<LiveSite> & { name: string }) => live.createStation(v),
      onSuccess: after,
    }),
    update: useMutation({
      mutationFn: (v: { id: string } & Partial<LiveSite>) => {
        const { id, ...rest } = v;
        return live.updateStation(id, rest);
      },
      onSuccess: after,
    }),
    archive: useMutation({ mutationFn: (id: string) => live.archiveStation(id), onSuccess: after }),
  };
}

/** What deleting a charge point would take with it. Fetched only when the
 *  dialog is open — it counts rows in every table that references the station,
 *  which is not something to do on every render of a list. */
export const useRemovalPreflight = (cpId: string | null) =>
  useQuery({
    queryKey: ['removal', cpId],
    queryFn: () => live.removalPreflight(cpId!),
    enabled: !!cpId,
    staleTime: 0,
  });

/** idTags that could start a charge. Only active ones on active drivers. */
export const useUsableIdTags = () =>
  useQuery({ queryKey: ['idtags'], queryFn: async () => (await live.usableIdTags()).items });

/**
 * The charger models a charge point may be set to.
 *
 * Changes rarely and the form needs it the moment it opens, so it does not
 * poll. Retired models come back too: a charge point already set to one has to
 * keep showing it rather than appearing blank and being silently changed on
 * the next save.
 */
export const useChargerModels = () =>
  useQuery({ queryKey: ['charger-models'], queryFn: () => live.allChargerModels() });

/**
 * Commands sent to a charger.
 *
 * These do not invalidate anything on success, on purpose. The charge point
 * list is not stale the moment a command is accepted — it becomes stale when
 * the charger acts, seconds later, and the list already polls every 20s. The
 * test bench polls faster while it is open rather than pretending the write
 * changed something.
 */
export function useChargerCommands() {
  const qc = useQueryClient();
  // Hoisted: hooks cannot be called from inside the object literal below.
  const afterWrite = useAfterWrite('chargepoints');
  return {
    start: useMutation({
      mutationFn: (v: {
        cpId: string; idTag: string; connectorId?: number; authorizeUnknown?: boolean;
      }): Promise<CommandResult> => live.remoteStart(v.cpId, {
        idTag: v.idTag, connectorId: v.connectorId, authorizeUnknown: v.authorizeUnknown,
      }),
      // Authorizing a tag adds it to the picker and to the drift report on
      // Drivers, so both are refetched when one was created.
      onSuccess: (r) => {
        if (!r.authorized) return;
        void qc.invalidateQueries({ queryKey: ['idtags'] });
        void qc.invalidateQueries({ queryKey: ['auth-report'] });
        void qc.invalidateQueries({ queryKey: ['activity'] });
      },
    }),
    setConfig: useMutation({
      mutationFn: (v: { cpId: string; key: string; value: string | number }) =>
        live.setChargerConfig(v.cpId, v.key, v.value),
    }),
    stop: useMutation({
      mutationFn: (v: { cpId: string; transactionId: string }): Promise<CommandResult> =>
        live.remoteStop(v.cpId, v.transactionId),
    }),
    /* Closes the CSMS row directly, for a session no charger will ever stop
       because it no longer knows about it. Invalidates like any other write:
       the connector is only free again once the list reflects it. */
    closeLost: useMutation({
      /* `force` overrides the server's refusal to close a session that is
         being held through an outage. An operator who knows the unit is dead
         needs a way through; nothing automatic passes it. */
      mutationFn: (v: { cpId: string; transactionId: string; force?: boolean }) =>
        live.closeLostSession(v.cpId, v.transactionId, v.force ?? false),
      onSuccess: afterWrite,
    }),
  };
}

/** Connector description. Status is never written from here — it is the
 *  charger's to report. */
export function useConnectorMutations() {
  const after = useAfterWrite('chargepoints');
  return {
    save: useMutation({
      mutationFn: (v: {
        cpId: string; connectorId: number; label?: string | null;
        connectorType?: string | null; powerType?: 'AC' | 'DC' | null;
        maxWatts?: number | null; declared?: boolean;
      }) => {
        const { cpId, connectorId, ...rest } = v;
        return live.saveConnector(cpId, connectorId, rest);
      },
      onSuccess: after,
    }),
    remove: useMutation({
      mutationFn: (v: { cpId: string; connectorId: number }) =>
        live.removeConnector(v.cpId, v.connectorId),
      onSuccess: after,
    }),
  };
}

export function useAgentMutations() {
  const after = useAfterWrite('agents');
  return {
    create: useMutation({
      mutationFn: (v: {
        name: string; area: string;
        commissionMode?: 'percent' | 'fixed'; commissionPct?: number; commissionFixed?: number;
      }) => live.createAgent(v),
      onSuccess: after,
    }),
    approve: useMutation({
      mutationFn: (id: string) => live.decideAgent(id, 'approved'),
      onSuccess: after,
    }),
    decline: useMutation({
      mutationFn: (v: { id: string; reason: string }) => live.decideAgent(v.id, 'declined', v.reason),
      onSuccess: after,
    }),
    issueLogin: useMutation({
      mutationFn: (v: { id: string; email?: string; phone?: string }) =>
        live.issueAgentLogin(v.id, { email: v.email, phone: v.phone }),
      onSuccess: after,
    }),
    updateCounter: useMutation({
      mutationFn: ({ id, ...v }: {
        id: string; counterName?: string | null; city?: string | null; address?: string | null;
        email?: string | null; phone?: string | null;
        lat?: number | null; lng?: number | null;
      }) => live.updateAgentCounter(id, v),
      onSuccess: after,
    }),
    reissueCode: useMutation({
      mutationFn: (id: string) => live.reissueAgentCode(id),
      onSuccess: after,
    }),
  };
}

export function useDriverMutations() {
  const after = useAfterWrite('drivers', 'driver', 'auth-report');
  return {
    create: useMutation({
      mutationFn: (v: { name: string; email?: string; phone?: string }) => live.createDriver(v),
      onSuccess: after,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; status?: string; docStatus?: string }) =>
        live.updateDriver(v.id, { status: v.status, docStatus: v.docStatus }),
      onSuccess: after,
    }),
    addVehicle: useMutation({
      mutationFn: (v: { driverId: string; name: string; plate: string; connector: string }) =>
        live.addVehicle(v.driverId, { name: v.name, plate: v.plate, connector: v.connector }),
      onSuccess: after,
    }),
    issueCard: useMutation({
      mutationFn: (v: { vehicleId: string; tag: string }) =>
        live.issueCard(v.vehicleId, v.tag),
      onSuccess: after,
    }),
    revokeCard: useMutation({
      mutationFn: (v: { id: string; reason?: string }) =>
        live.revokeCard(v.id, v.reason),
      onSuccess: after,
    }),
  };
}

export function useUserMutations() {
  // Account changes belong in the audit trail, so it is refetched with them.
  const after = useAfterWrite('console-users');
  return {
    create: useMutation({
      mutationFn: (v: { email: string; name: string; role: string }) => live.createUser(v),
      onSuccess: after,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; role?: string; status?: string }) =>
        live.updateUser(v.id, { role: v.role, status: v.status }),
      onSuccess: after,
    }),
  };
}

export function useConfigMutation() {
  const after = useAfterWrite('config');
  return useMutation({ mutationFn: (v: Record<string, string>) => live.setConfig(v), onSuccess: after });
}

export function useReleaseMutations() {
  const after = useAfterWrite('releases');
  return useMutation({
    mutationFn: ({ app, platform, ...v }: {
      app: 'driver' | 'agent';
      platform: 'android' | 'ios';
      forceUpdate?: boolean; rollout?: number; published?: boolean; minSupported?: string;
    }) => live.updateRelease(app, platform, v),
    onSuccess: after,
  });
}

export function useBuildUpload() {
  const after = useAfterWrite('releases');
  return useMutation({
    mutationFn: (v: { app: 'driver' | 'agent'; file: File; version: string; build: number; notes?: string }) =>
      live.uploadBuild(v.app, v.file, v),
    onSuccess: after,
  });
}
